import { memo, useCallback, useMemo } from 'react';
import { Group, Line } from 'react-konva';
import Konva from 'konva';
import { useDispatch, useSelector } from 'react-redux';
import { useKonvaImageScale } from 'contexts/ToolDimensionsAndScaleContext';
import { RootState } from 'store';
import {
	setHoveredLines,
	setUpateEffectedFacets,
	updateAddGutterLine,
	updateLineType,
	updateRemoveGutterLine
} from 'store/slices/RoofToolLinesSlice';
import { GUTTER_LINE_CONFIG, EDGE_HIT_STROKE, GUTTER_EDGE_TYPE } from './constant';
import useRoofLines from './useRoofLines';
import { KonvaEventObject } from 'konva/lib/Node';
import {
	areEdgesEqual, buildSpecialEdgePayload, getMousePositionLines,
	isSpecialFacetLine
} from './utils';
import { selectClassifiedLineTypesByCoordId } from 'store/slices/RoofDataSlice/roofSelector';
import TempGutterLines from './TempGutterLines';

interface Props extends Konva.LineConfig {
	segmentId: string;
	coordinates: number[][];
	edges: EdgeData[];
}

const RoofToolLines = ({ segmentId, edges }: Props) => {
	const roofLines = useRoofLines();
	const dispatch = useDispatch();
	const scale = useKonvaImageScale();
	const { zoomLevel } = useSelector((state: RootState) => state.toolNewPostions.data);

	const isTouchDevice = useMemo(() => {
		return (
			'ontouchstart' in window ||
			navigator.maxTouchPoints > 0
		);
	}, []);

	const edgeHitStroke = useMemo(() => {
		if (isTouchDevice) {
			const hitStroke = Math.max(EDGE_HIT_STROKE, 30 / zoomLevel);
			return hitStroke;
		}
		return EDGE_HIT_STROKE;
	}, [isTouchDevice, zoomLevel]);

	const {
		currentFacetIndex,
		currentSelectedLine,
		selectedFacets,
		hoverLinesIndex,
		addGutterLine,
		removeGutterLine,
		createSpecialEdges,
		updateSpecialEdges,
		effectedFacets,
		activeGutterLine,
	} = useSelector((state: RootState) => state.roofToolLines.data);

	const { specialEdges: updatedEdges, allGutterLines: storedGutterLines } = useSelector((state: RootState) => state.roofData.data);
	const { loading: autoDetectionInProgress } = useSelector((state: RootState) => state.roofToolLines.autoEdgeDetection);
	const classifiedLineTypesByCoordId = useSelector(selectClassifiedLineTypesByCoordId);

	const { selectedLines = [], newAddedGutterLines = [], removedGutterLineIds = [], gutterLines = [] } = selectedFacets[segmentId] ?? {};

	const getSelectedLineType = useCallback((facetId: string, edgeId: number) => {
		return selectedFacets[facetId]?.selectedLines.find(line => line.id === edgeId)?.lineType;
	}, [selectedFacets]);

	const handleLineClick = useCallback((e: KonvaEventObject<PointerEvent>, edgeData: EdgeData) => {
		if(effectedFacets.length > 0) return;
		const currentEdgeTypeNeedsFacetSelection = isSpecialFacetLine(currentSelectedLine);
		const { edgeLines: currentLines, gutterLines } = getMousePositionLines(e, edgeHitStroke);

		let updateLines = currentLines.map(eachLine => {
			const currentLine = eachLine.attrs.data;
			const currentFacetId: string = currentLine.segmentId;
			const currentLineId: number = +currentLine.id;
			const specialLineCase = currentEdgeTypeNeedsFacetSelection && currentLines.length > 1;
			const discard = specialLineCase ? true : false;
			return { id: currentLineId, lineType: currentSelectedLine || '', facetId: currentFacetId, discard };
		});
		const uniqueFacets = currentLines.reduce((acc: string[], eachLine) => {
			const facetId = eachLine.attrs.data.segmentId;
			if (!acc.includes(facetId)) {
				acc.push(facetId);
			}
			return acc;
		}, []);
		console.log(currentLines, uniqueFacets);

		if (addGutterLine) {
			let filterDiscard = false;
			const updateGutterLines = [];
			const edgeAlreadyHasGutterLine = !!gutterLines?.length;
			if(edgeAlreadyHasGutterLine) return;

			// 502.77-447.666_504.033-439.237
			const existingGutterLineIndex = storedGutterLines.findIndex(gl => areEdgesEqual(gl.coords, edgeData.coords));

			if(existingGutterLineIndex !== -1){
				const existingGutterLine = storedGutterLines[existingGutterLineIndex];
				dispatch(updateRemoveGutterLine({
					id:existingGutterLine.id, 
					facetId:existingGutterLine.roofSegmentId!
				}));
			}

			for(const eachLine of currentLines){
				const currentLine = eachLine.attrs.data;
				const facetId: string = currentLine.segmentId;

				const discard = currentLines.length > 1 ? currentFacetIndex !== facetId : false;
				if (currentLines.length > 1 && currentFacetIndex === facetId) {
					filterDiscard = true;
				}
				updateGutterLines.push( { facetId, id: edgeData.coordId, coords: edgeData.coords, discard });
			}

			if (uniqueFacets.length > 1 && updateGutterLines.length > 1) {
				const effectedFacet = updateGutterLines.map(eachLine => ({facetId: eachLine.facetId, lineId: eachLine.id}));
				dispatch(setUpateEffectedFacets({ facets: effectedFacet }));
			}

			// any edge can carry a gutter line, and it gets classified as an eave along with it,
			// whatever type it had before (a ridge/hip/rake picked here becomes an eave)
			const eaveLines = updateLines.map(eachLine => ({
				...eachLine,
				lineType: GUTTER_EDGE_TYPE,
				discard: currentLines.length > 1 ? currentFacetIndex !== eachLine.facetId : false,
				currentLineType: getSelectedLineType(eachLine.facetId, eachLine.id)
			}));

			const needsEaveClassification = eaveLines.some(({ currentLineType }) => currentLineType !== GUTTER_EDGE_TYPE);
			const eaveUpdateLines = eaveLines.map(({ currentLineType, ...eachLine }) => eachLine);

			const edgeTypeUpdate = needsEaveClassification
				? {
					updateLines: eaveUpdateLines,
					...buildSpecialEdgePayload(eaveUpdateLines, GUTTER_EDGE_TYPE, updatedEdges)
				}
				: undefined;

			dispatch(updateAddGutterLine({ updateLines: updateGutterLines, edgeTypeUpdate }));
		} else if (currentSelectedLine) {
			if (currentEdgeTypeNeedsFacetSelection) {
				updateLines = updateLines.map(eachLine => ({
					...eachLine,
					discard: updateLines.length > 1 ? currentFacetIndex !== eachLine.facetId : false
				}));
				if (uniqueFacets.length > 1 && updateLines.length > 1) {
					const effectedFacet = updateLines.map(eachLine => ({facetId: eachLine.facetId, lineId: eachLine.id}));
					dispatch(setUpateEffectedFacets({ facets: effectedFacet }));
				}
			}
			const { createSpecialEdgesData, updateSpecialEdgesData } = buildSpecialEdgePayload(
				updateLines,
				currentSelectedLine,
				updatedEdges
			);
			dispatch(updateLineType({ updateLines, createSpecialEdgesData, updateSpecialEdgesData }));
			dispatch(setHoveredLines({linesIndex:[]}));
		}
	}, [addGutterLine, currentSelectedLine, dispatch, currentFacetIndex, updatedEdges, createSpecialEdges,
		updateSpecialEdges, selectedFacets, effectedFacets, scale, getSelectedLineType]);

	const getLineStyle = useCallback((lineId: number, coordId: string) => {

		const selectedLine = selectedLines.find(line => line.id == lineId);
		const isHovered = hoverLinesIndex.includes(`${segmentId}-${lineId}`);
		const isEffected = effectedFacets.some(eachLine => eachLine.lineId === lineId);

		if (isHovered && effectedFacets.length > 0) {
			if (!isEffected) {
				return { color: 'transparent', dashed: [], strokeWidth: 1 };
			}
			// return { ...roofLines[(selectedLine?.lineType || currentSelectedLine || '')], strokeWidth: 1 };
		}

		// every edge can take a gutter line, so preview each hovered one in the gutter colour
		if (isHovered && addGutterLine) {
			return { color: GUTTER_LINE_CONFIG.color, dashed: [], strokeWidth: GUTTER_LINE_CONFIG.strokeWidth };
		}

		if (isHovered && currentSelectedLine) {
			return { ...roofLines[currentSelectedLine], strokeWidth: 1 };
		}

		// a shared edge is drawn once per facet that meets along it, and the two sides can carry
		// different types. Both copies take the strongest claim on the edge (user mapped, then
		// system detected, then none) so they agree with each other and with the view mode
		// instead of whichever facet happens to render last winning.
		const lineType = classifiedLineTypesByCoordId.get(coordId) ?? selectedLine?.lineType;

		if (lineType && roofLines[lineType]) {
			return { ...roofLines[lineType], strokeWidth: 1 };
		}

		return { color: 'transparent', dashed: [], strokeWidth: 1 };
	}, [currentSelectedLine, currentFacetIndex, selectedLines, hoverLinesIndex, segmentId, addGutterLine,
		createSpecialEdges, updateSpecialEdges, effectedFacets, classifiedLineTypesByCoordId, roofLines]);
	// const listeningLines = isCurrentFacet && (!!currentSelectedLine || activeGutterLine) && !removeGutterLine;
	const listeningLines = !removeGutterLine && (effectedFacets.length === 0);

	const handlePointerEnter = (e: Konva.KonvaEventObject<PointerEvent>) => {
		dispatch(setHoveredLines({ linesIndex: [] }));
		const stage = e.target.getStage();
		if (stage && effectedFacets.length > 1) {
			stage.container().style.cursor = 'not-allowed';
		} else if (stage && addGutterLine) {
			stage.container().style.cursor = 'pointer';
		}
		const hoveredLines = getMousePositionLines(e, edgeHitStroke)?.edgeLines?.map(eachLine => eachLine.attrs.name.replace('edge-lines-', ''));
		dispatch(setHoveredLines({ linesIndex: hoveredLines }));
	};

	return (
		<Group listening={!autoDetectionInProgress}>
			{/* ----------- Already existing gutter-lines ---------- */}
			{
				gutterLines?.map(gl => {
					if(removedGutterLineIds.includes(gl.id)) return null;
					return (<TempGutterLines
						key={gl.id}
						segmentId={segmentId}
						gutterLine={gl}
						points={gl.coords.flat()}
						listening={activeGutterLine}
						scale={scale}
						hitStroke={edgeHitStroke}
					/>);
				})
			}
			{/* ----------- Roof edges lines ---------- */}
			{edges.map((edgeData, edgeIndex) => {
				const { id, coords, coordId } = edgeData;
				const { color, dashed, strokeWidth } = getLineStyle(id, coordId);
				const gutterEdge = newAddedGutterLines?.find(line => line.id === coordId);
				const commonLineProps = {
					points: coords.flat(),
					scale,
					strokeWidth
				};

				// a gutter can be added on any edge, which is then classified as an eave as well
				const canListen = listeningLines;

				return (
					<Group key={edgeIndex}>
						{/* edges indentified as gutter */}
						{gutterEdge && (
							<TempGutterLines
								segmentId={segmentId}
								gutterLine={gutterEdge}
								{...commonLineProps}
								hitStroke={edgeHitStroke}
								listening={activeGutterLine}
							/>
						)}

						<Line
							{...commonLineProps}
							name={`edge-lines-${segmentId}-${id}`}
							stroke={color}
							data={{ segmentId, id, specialEdge: edgeData.specialEdge }}
							hitStrokeWidth={edgeHitStroke}
							dash={dashed}
							listening={canListen}
							onPointerEnter={(e) => handlePointerEnter(e)}
							onPointerOut={(e) => {
								const stage = e.target.getStage();
								if (stage && (effectedFacets.length > 1 || addGutterLine)) {
									stage.container().style.cursor = '';
								}
								dispatch(setHoveredLines({ linesIndex: [] }));
							}}
							onPointerClick={(e) => handleLineClick(e, edgeData)}
						/>
					</Group>
				);
			})}
		</Group>
	);
};

export default memo(RoofToolLines);
